import React, { useContext, useEffect, useState } from 'react';
import { Container, Table, Button, Row, Col, Input, Form, FormGroup, Label, Badge, Card, CardBody } from 'reactstrap';
import PaginationMaterial from '../../components/PaginationMaterial/PaginationMaterial';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import Header from '../../components/Headers/Header';
import AdminNavbar from '../../components/Navbars/AdminNavbar/AdminNavbar';
import { AuthContext } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { CreateVendaModal } from '../../components/Venda/CreateVendaModal';
import ModalConfirm from '../../components/ModalConfirm/ModalConfirm';
import api from '../../api';
import Select from 'react-select';
import { RelatorioVendaContext } from '../../context/RelatorioVendaContext';

interface VendaDTO {
    id: number;
    produtoId: number;
    produtoName: string;
    clienteId: number;
    clienteName: string;
    vendedorId: number;
    vendedorName: string;
    hasBoleto: boolean;
    formaPagamentoValues: {
        id: number;
        formaPagamentoName: string | null;
        value: number;
    }[];
}

interface VendaPagedResponse {
    result: VendaDTO[];
    pageCount: number;
}

const Venda = () => {
    const showToast = useToast();
    const { objUserTk, handleLogout } = useContext(AuthContext);
    const {
        listVendasNaoRealizadasEmpresa,
        vendasNaoRealizadasEmpresaPageCount,
        getVendasNaoRealizadasByEmpresa,
        createVendaNaoRealizada,
        updateVendaNaoRealizada,
    } = useContext(RelatorioVendaContext);

    const [activeTab, setActiveTab] = useState<'vendas' | 'naoRealizadas'>('vendas');

    const [loading, setLoading] = useState(true);
    const [downloadingBoleto, setDownloadingBoleto] = useState<number | null>(null);
    const [vendas, setVendas] = useState<VendaDTO[]>([]);
    const [pageCount, setPageCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 9;

    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [vendaToDelete, setVendaToDelete] = useState<number | null>(null);

    // Filtros - aba vendas
    const [vendedorIdFilter, setVendedorIdFilter] = useState(0);
    const [formaPagamentoIdFilter, setFormaPagamentoIdFilter] = useState(0);

    // Filtros - aba não realizadas
    const [nrStatusFilter, setNrStatusFilter] = useState(0);
    const [nrVendedorIdFilter, setNrVendedorIdFilter] = useState(0);
    const [nrAlunoNomeFilter, setNrAlunoNomeFilter] = useState('');
    const [nrCurrentPage, setNrCurrentPage] = useState(1);
    const nrPageSize = 10;

    // Estado de formulários de tentativa (por relacaoId)
    const [tentativasForm, setTentativasForm] = useState<Record<number, { status: number; observacao: string }>>({});

    // Listas para filtros
    const [vendedoresList, setVendedoresList] = useState<{ id: number; name: string }[]>([]);
    const [formasPagamentoList, setFormasPagamentoList] = useState<{ id: number; name: string }[]>([]);

    useEffect(() => {
        loadVendas(1);
        loadVendedores();
        loadFormasPagamento();
    }, []);

    useEffect(() => {
        if (activeTab === 'naoRealizadas') {
            loadVendasNaoRealizadas(1);
        }
    }, [activeTab]);

    const loadVendas = async (page: number, vendedorId = 0, formaPagamentoId = 0) => {
        setLoading(true);
        try {
            const response = await api.get<VendaPagedResponse>('/Venda/getAllVendaPaged', {
                params: {
                    EmpresaId: objUserTk.EmpresaId,
                    PageNumber: page,
                    PageSize: pageSize,
                    VendedorId: vendedorId || undefined,
                    FormaPagamentoId: formaPagamentoId || undefined,
                },
            });

            if (response.data) {
                setVendas(response.data.result || []);
                setPageCount(response.data.pageCount || 0);
                setCurrentPage(page);
            }
        } catch (error: any) {
            if (error.response?.status === 401) {
                handleLogout();
                showToast('error', 'Sessão expirada. Faça login novamente.');
            } else {
                showToast('error', 'Erro ao carregar vendas.');
            }
        } finally {
            setLoading(false);
        }
    };

    const loadVendedores = async () => {
        try {
            const response = await api.get('/Terceiro/getTerceiroPaged', {
                params: {
                    pageNumber: 1,
                    pageSize: 1000,
                    nome: '',
                    empresaId: objUserTk.EmpresaId,
                    tipoTerceiro: 'Vendedor',
                },
            });

            if (response.data?.result) {
                setVendedoresList(response.data.result);
            }
        } catch (error) {
            console.error('Erro ao carregar vendedores:', error);
        }
    };

    const loadFormasPagamento = async () => {
        try {
            const response = await api.get(`/FormaPagamento/getFormaPagamentoByEmpresaId/${objUserTk.EmpresaId}`);
            if (response.data) {
                setFormasPagamentoList(response.data);
            }
        } catch (error) {
            console.error('Erro ao carregar formas de pagamento:', error);
        }
    };

    const loadVendasNaoRealizadas = (page: number, statusF = nrStatusFilter, vendedorF = nrVendedorIdFilter, nomeF = nrAlunoNomeFilter) => {
        getVendasNaoRealizadasByEmpresa(objUserTk.EmpresaId, page, nrPageSize, {
            status: statusF || undefined,
            vendedorId: vendedorF || undefined,
            alunoNome: nomeF || undefined,
        }).then(() => setNrCurrentPage(page));
    };

    const handlePageChange = (_: any, value: number) => {
        loadVendas(value, vendedorIdFilter, formaPagamentoIdFilter);
    };

    const handleFilterSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        loadVendas(1, vendedorIdFilter, formaPagamentoIdFilter);
    };

    const handleClearFilters = () => {
        setVendedorIdFilter(0);
        setFormaPagamentoIdFilter(0);
        loadVendas(1, 0, 0);
    };

    const handleNrFilterSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        loadVendasNaoRealizadas(1);
    };

    const handleNrClearFilters = () => {
        setNrStatusFilter(0);
        setNrVendedorIdFilter(0);
        setNrAlunoNomeFilter('');
        loadVendasNaoRealizadas(1, 0, 0, '');
    };

    const handleDeleteVenda = async () => {
        if (!vendaToDelete) return;

        try {
            await api.delete(`/Venda/${vendaToDelete}`);
            showToast('success', 'Venda excluída com sucesso!');
            setDeleteModalOpen(false);
            setVendaToDelete(null);
            loadVendas(currentPage, vendedorIdFilter, formaPagamentoIdFilter);
        } catch (error: any) {
            if (error.response?.status === 401) {
                handleLogout();
                showToast('error', 'Sessão expirada. Faça login novamente.');
            } else if (error.response?.status === 404) {
                showToast('error', 'Venda não encontrada. Ela pode já ter sido excluída.');
                setDeleteModalOpen(false);
                setVendaToDelete(null);
                loadVendas(currentPage, vendedorIdFilter, formaPagamentoIdFilter);
            } else if (error.response?.status === 400) {
                showToast('error', error.response.data || 'Erro ao excluir a venda.');
            } else {
                showToast('error', 'Erro ao excluir a venda.');
            }
        }
    };

    const handleDownloadContrato = async (vendaId: number) => {
        try {
            const response = await api.get(`/Venda/contrato/${vendaId}`, {
                responseType: 'blob',
            });

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `contrato_venda_${vendaId}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            showToast('success', 'Contrato baixado com sucesso!');
        } catch (error: any) {
            if (error.response?.status === 401) {
                handleLogout();
                showToast('error', 'Sessão expirada. Faça login novamente.');
            } else if (error.response?.status === 404) {
                showToast('error', 'Contrato não encontrado.');
            } else {
                showToast('error', 'Erro ao baixar contrato.');
            }
        }
    };

    const handleDownloadBoleto = async (vendaId: number) => {
        setDownloadingBoleto(vendaId);
        try {
            const response = await api.get(`/Venda/${vendaId}/boleto`, {
                responseType: 'blob',
            });

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `boleto_venda_${vendaId}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            showToast('success', 'Boleto baixado com sucesso!');
        } catch (error: any) {
            if (error.response?.status === 401) {
                handleLogout();
                showToast('error', 'Sessão expirada. Faça login novamente.');
            } else if (error.response?.status === 404) {
                showToast('error', 'Boleto não encontrado para esta venda.');
            } else {
                showToast('error', 'Erro ao baixar boleto.');
            }
        } finally {
            setDownloadingBoleto(null);
        }
    };

    const getTotalVenda = (venda: VendaDTO) => {
        return venda.formaPagamentoValues.reduce((sum, fp) => sum + fp.value, 0);
    };

    const formatCurrency = (value: number) => {
        return `R$ ${value.toFixed(2).replace('.', ',')}`;
    };

    const getVendaNaoRealizadaStatusLabel = (status: number) => {
        switch (status) {
            case 1: return 'Em aberto';
            case 2: return 'Nova tentativa';
            case 3: return 'Descartado';
            default: return 'Desconhecido';
        }
    };

    const getTentativaForm = (relacaoId: number) =>
        tentativasForm[relacaoId] || { status: 2, observacao: '' };

    const setTentativaFormValue = (relacaoId: number, field: 'status' | 'observacao', value: string | number) => {
        setTentativasForm(prev => ({
            ...prev,
            [relacaoId]: { ...getTentativaForm(relacaoId), [field]: value },
        }));
    };

    const registrarTentativa = async (relacaoId: number) => {
        const form = getTentativaForm(relacaoId);
        if (!form.observacao.trim()) {
            showToast('error', 'Informe uma observação para a tentativa.');
            return;
        }

        const result = await createVendaNaoRealizada({
            relatorioVendaAlunoVendedorId: relacaoId,
            status: form.status,
            observacao: form.observacao,
        });

        if (result) {
            setTentativasForm(prev => ({ ...prev, [relacaoId]: { status: 2, observacao: '' } }));
            loadVendasNaoRealizadas(nrCurrentPage);
        }
    };

    const vendedorOptions = [
        { value: 0, label: 'Todos' },
        ...vendedoresList.map(v => ({ value: v.id, label: v.name })),
    ];

    return (
        <>
            <LoadingSpinner />
            <Header />
            <AdminNavbar brandText="Gerenciar Vendas" search={false} titleSearch="" />

            <Container className="mt-5">
                {/* Cabeçalho com Título e Botão */}
                <Row className="align-items-center mb-4">
                    <Col xs="12" md="6">
                        <div>
                            <h2 className="mb-1">
                                <i className="fa fa-shopping-cart mr-2 text-primary"></i>
                                Gerenciar Vendas
                            </h2>
                            <p className="text-muted mb-0">Cadastre e gerencie as vendas de produtos e serviços</p>
                        </div>
                    </Col>
                    {activeTab === 'vendas' && (
                        <Col xs="12" md="6" className="text-right">
                            <Button
                                style={{ color: "#FFF", backgroundColor: "#585858" }}
                                className="btn-filter"
                                onClick={() => setCreateModalOpen(true)}
                                size="lg"
                            >
                                <i className="fa fa-plus mr-2"></i>
                                Nova Venda
                            </Button>
                        </Col>
                    )}
                </Row>

                {/* Abas */}
                <div className="d-flex mb-4" style={{ gap: '8px' }}>
                    <Button
                        type="button"
                        style={{
                            color: activeTab === 'vendas' ? '#FFF' : '#585858',
                            backgroundColor: activeTab === 'vendas' ? '#585858' : '#f4f5f7',
                        }}
                        onClick={() => setActiveTab('vendas')}
                    >
                        Vendas
                    </Button>
                    <Button
                        type="button"
                        style={{
                            color: activeTab === 'naoRealizadas' ? '#FFF' : '#585858',
                            backgroundColor: activeTab === 'naoRealizadas' ? '#585858' : '#f4f5f7',
                        }}
                        onClick={() => setActiveTab('naoRealizadas')}
                    >
                        Não Realizadas
                    </Button>
                </div>

                <hr className="my-4" />

                {/* ===================== ABA: VENDAS ===================== */}
                {activeTab === 'vendas' && (
                    <>
                        {/* Filtros */}
                        <Card className="shadow-sm mb-4">
                            <CardBody>
                                <h5 className="mb-3">
                                    <i className="fa fa-filter mr-2"></i>
                                    Filtros de Busca
                                </h5>
                                <Form onSubmit={handleFilterSubmit}>
                                    <Row>
                                        <Col md={5}>
                                            <FormGroup>
                                                <Label>Vendedor</Label>
                                                <Select
                                                    options={[
                                                        { value: 0, label: 'Todos' },
                                                        ...vendedoresList.map(v => ({ value: v.id, label: v.name }))
                                                    ]}
                                                    value={
                                                        vendedorIdFilter
                                                            ? { value: vendedorIdFilter, label: vendedoresList.find(v => v.id === vendedorIdFilter)?.name || '' }
                                                            : { value: 0, label: 'Todos' }
                                                    }
                                                    onChange={(option: any) => setVendedorIdFilter(option?.value || 0)}
                                                    placeholder="Selecione..."
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={5}>
                                            <FormGroup>
                                                <Label>Forma de Pagamento</Label>
                                                <Select
                                                    options={[
                                                        { value: 0, label: 'Todas' },
                                                        ...formasPagamentoList.map(f => ({ value: f.id, label: f.name }))
                                                    ]}
                                                    value={
                                                        formaPagamentoIdFilter
                                                            ? { value: formaPagamentoIdFilter, label: formasPagamentoList.find(f => f.id === formaPagamentoIdFilter)?.name || '' }
                                                            : { value: 0, label: 'Todas' }
                                                    }
                                                    onChange={(option: any) => setFormaPagamentoIdFilter(option?.value || 0)}
                                                    placeholder="Selecione..."
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={2} className="d-flex align-items-end">
                                            <FormGroup className="w-100">
                                                <Button
                                                    style={{ color: "#FFF", backgroundColor: "#585858" }}
                                                    type="submit"
                                                    block
                                                >
                                                    <i className="fa fa-search mr-2"></i>
                                                    Filtrar
                                                </Button>
                                                <Button
                                                    style={{ color: "#FFF", backgroundColor: "#d9534f" }}
                                                    block
                                                    onClick={handleClearFilters}
                                                    className="mt-2"
                                                >
                                                    Limpar
                                                </Button>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </Form>
                            </CardBody>
                        </Card>

                        {/* Tabela de Vendas */}
                        <Row className="mt-4">
                            <Col>
                                <Table hover responsive className="table-striped custom-table">
                                    <thead>
                                        <tr>
                                            <th>Nº Venda</th>
                                            <th>Cliente</th>
                                            <th>Produto</th>
                                            <th>Vendedor</th>
                                            <th>Formas de Pagamento</th>
                                            <th>Total</th>
                                            <th>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {vendas.length === 0 ? (
                                            <tr>
                                                <td colSpan={7} className="text-center">
                                                    <em>Nenhuma venda cadastrada.</em>
                                                </td>
                                            </tr>
                                        ) : (
                                            vendas.map((venda) => (
                                                <tr key={venda.id}>
                                                    <td>
                                                        <Badge color="primary" pill>
                                                            #{venda.id}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <div>
                                                            <strong>{venda.clienteName}</strong>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div>
                                                            <i className="fa fa-box mr-2 text-muted"></i>
                                                            {venda.produtoName}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div>
                                                            <i className="fa fa-user-tie mr-2 text-muted"></i>
                                                            {venda.vendedorName}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {venda.formaPagamentoValues.map((fp, index) => (
                                                            <div key={fp.id || index} className="mb-1">
                                                                <Badge color="info" className="mr-1">
                                                                    {fp.formaPagamentoName || 'Não definida'}
                                                                </Badge>
                                                                <small>{formatCurrency(fp.value)}</small>
                                                            </div>
                                                        ))}
                                                    </td>
                                                    <td>
                                                        <h5 className="mb-0 text-success">
                                                            {formatCurrency(getTotalVenda(venda))}
                                                        </h5>
                                                    </td>
                                                    <td>
                                                        <Button
                                                            title="Baixar Contrato PDF"
                                                            onClick={() => handleDownloadContrato(venda.id)}
                                                        >
                                                            <i className="fa fa-file-pdf"></i>
                                                        </Button>{' '}
                                                        {venda.hasBoleto && (
                                                            <Button
                                                                color="info"
                                                                title="Baixar Boleto"
                                                                onClick={() => handleDownloadBoleto(venda.id)}
                                                                disabled={downloadingBoleto === venda.id}
                                                            >
                                                                {downloadingBoleto === venda.id
                                                                    ? <i className="fa fa-spinner fa-spin"></i>
                                                                    : <i className="fa fa-barcode"></i>
                                                                }
                                                            </Button>
                                                        )}{' '}
                                                        <Button
                                                            color="danger"
                                                            title="Excluir Venda"
                                                            onClick={() => {
                                                                setVendaToDelete(venda.id);
                                                                setDeleteModalOpen(true);
                                                            }}
                                                        >
                                                            <i className="fa fa-trash"></i>
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>

                                {/* Paginação */}
                                {pageCount > 1 && (
                                    <div className="d-flex justify-content-center mt-4">
                                        <PaginationMaterial
                                            count={pageCount}
                                            onChange={handlePageChange}
                                        />
                                    </div>
                                )}
                            </Col>
                        </Row>
                    </>
                )}

                {/* ===================== ABA: NÃO REALIZADAS ===================== */}
                {activeTab === 'naoRealizadas' && (
                    <>
                        {/* Filtros */}
                        <Card className="shadow-sm mb-4">
                            <CardBody>
                                <h5 className="mb-3">
                                    <i className="fa fa-filter mr-2"></i>
                                    Filtros
                                </h5>
                                <Form onSubmit={handleNrFilterSubmit}>
                                    <Row>
                                        <Col md={3}>
                                            <FormGroup>
                                                <Label>Nome do Aluno</Label>
                                                <Input
                                                    className="form-control-alternative"
                                                    type="text"
                                                    value={nrAlunoNomeFilter}
                                                    placeholder="Buscar por nome"
                                                    onChange={(e) => setNrAlunoNomeFilter(e.target.value)}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={3}>
                                            <FormGroup>
                                                <Label>Vendedor</Label>
                                                <Select
                                                    options={vendedorOptions}
                                                    value={vendedorOptions.find(o => o.value === nrVendedorIdFilter) || vendedorOptions[0]}
                                                    onChange={(opt: any) => setNrVendedorIdFilter(opt?.value || 0)}
                                                    placeholder="Selecione..."
                                                    isClearable
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={3}>
                                            <FormGroup>
                                                <Label>Status</Label>
                                                <Input
                                                    className="form-control-alternative"
                                                    type="select"
                                                    value={nrStatusFilter}
                                                    onChange={(e) => setNrStatusFilter(Number(e.target.value))}
                                                >
                                                    <option value={0}>Todos</option>
                                                    <option value={1}>Em aberto</option>
                                                    <option value={2}>Nova tentativa</option>
                                                    <option value={3}>Descartado</option>
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                        <Col md={3} className="d-flex align-items-end">
                                            <FormGroup className="w-100">
                                                <Button
                                                    style={{ color: '#FFF', backgroundColor: '#585858' }}
                                                    type="submit"
                                                    block
                                                >
                                                    <i className="fa fa-search mr-2"></i>
                                                    Filtrar
                                                </Button>
                                                <Button
                                                    style={{ color: '#FFF', backgroundColor: '#d9534f' }}
                                                    block
                                                    onClick={handleNrClearFilters}
                                                    className="mt-2"
                                                >
                                                    Limpar
                                                </Button>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </Form>
                            </CardBody>
                        </Card>

                        {/* Tabela de registros + ação de nova tentativa */}
                        <h5 className="mb-3">Histórico de vendas não realizadas</h5>
                        <Table striped responsive className="table-striped">
                            <thead>
                                <tr>
                                    <th>Data</th>
                                    <th>Código</th>
                                    <th>Aluno</th>
                                    <th>Vendedor</th>
                                    <th>Curso</th>
                                    <th>Status</th>
                                    <th>Observação</th>
                                    <th>Nova tentativa</th>
                                </tr>
                            </thead>
                            <tbody>
                                {listVendasNaoRealizadasEmpresa.length === 0 ? (
                                    <tr>
                                        <td colSpan={8} className="text-center text-danger">
                                            Nenhum registro encontrado.
                                        </td>
                                    </tr>
                                ) : (
                                    listVendasNaoRealizadasEmpresa.map((item) => {
                                        const form = getTentativaForm(item.relatorioVendaAlunoVendedorId);
                                        const isDescartado = item.status === 3;
                                        return (
                                            <tr key={item.id}>
                                                <td style={{ whiteSpace: 'nowrap' }}>
                                                    {item.createdDate ? new Date(item.createdDate).toLocaleString('pt-BR') : '-'}
                                                </td>
                                                <td>{item.alunoReferenceCode || item.alunoId}</td>
                                                <td>{item.alunoNome}</td>
                                                <td>{item.vendedorNome}</td>
                                                <td>{item.cursoNome}</td>
                                                <td>
                                                    <Badge
                                                        color={item.status === 3 ? 'danger' : item.status === 1 ? 'warning' : 'info'}
                                                        pill
                                                    >
                                                        {getVendaNaoRealizadaStatusLabel(item.status)}
                                                    </Badge>
                                                </td>
                                                <td>{item.observacao}</td>
                                                <td style={{ minWidth: 280 }}>
                                                    {isDescartado ? (
                                                        <span className="text-muted">Encerrado</span>
                                                    ) : (
                                                        <div className="d-flex flex-column" style={{ gap: 4 }}>
                                                            <Input
                                                                className="form-control-alternative"
                                                                type="select"
                                                                value={form.status}
                                                                onChange={(e) => setTentativaFormValue(item.relatorioVendaAlunoVendedorId, 'status', Number(e.target.value))}
                                                                style={{ marginBottom: 4 }}
                                                            >
                                                                <option value={1}>Em aberto</option>
                                                                <option value={2}>Nova tentativa</option>
                                                                <option value={3}>Descartado</option>
                                                            </Input>
                                                            <Input
                                                                className="form-control-alternative"
                                                                type="textarea"
                                                                rows={2}
                                                                value={form.observacao}
                                                                placeholder="Observação da tentativa"
                                                                onChange={(e) => setTentativaFormValue(item.relatorioVendaAlunoVendedorId, 'observacao', e.target.value)}
                                                            />
                                                            <Button
                                                                type="button"
                                                                size="sm"
                                                                style={{ color: '#FFF', backgroundColor: '#585858', alignSelf: 'flex-start' }}
                                                                onClick={() => registrarTentativa(item.relatorioVendaAlunoVendedorId)}
                                                            >
                                                                Registrar
                                                            </Button>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </Table>

                        {vendasNaoRealizadasEmpresaPageCount > 1 && (
                            <div className="d-flex justify-content-center mt-4">
                                <PaginationMaterial
                                    count={vendasNaoRealizadasEmpresaPageCount}
                                    onChange={(_: any, value: number) => loadVendasNaoRealizadas(value)}
                                />
                            </div>
                        )}
                    </>
                )}
            </Container>

            {/* Modal de Criação */}
            <CreateVendaModal
                isOpen={createModalOpen}
                toggle={() => setCreateModalOpen(false)}
                onSuccess={() => {
                    setCreateModalOpen(false);
                    loadVendas(currentPage, vendedorIdFilter, formaPagamentoIdFilter);
                }}
            />

            {/* Modal de Confirmação de Exclusão */}
            <ModalConfirm
                openModal={deleteModalOpen}
                closeModal={() => {
                    setDeleteModalOpen(false);
                    setVendaToDelete(null);
                }}
                handleConfirm={handleDeleteVenda}
                title="Excluir venda"
                message="Você realmente deseja excluir esta venda? Esta ação não pode ser desfeita."
            />
        </>
    );
};

export default Venda;
